import type { PlanFile, PresignResponse } from "@klantpunt/shared";
import { api, ApiError } from "./api";
import { compressPlanImage, isPdf, type FileInfo } from "./pdf";

/** Tekening uploaden: presign → direct naar de opslag (R2) → bevestigen. */
export async function uploadPlan(file: File, info: FileInfo): Promise<PlanFile> {
  const blob = isPdf(file) ? file : await compressPlanImage(file);
  const mime = isPdf(file) ? "application/pdf" : blob.type || file.type || "image/jpeg";
  const id = crypto.randomUUID();
  const presign = await api<PresignResponse>("/files/presign", {
    method: "POST",
    body: { id, kind: "plan", mime, size: blob.size, name: file.name },
  });
  await putToStorage(presign, blob);
  return api<PlanFile>(`/files/${id}/confirm`, {
    method: "POST",
    body: { pages: info.pages, width: info.width, height: info.height },
  });
}

/** Afwijking van de paginaverhouding (0.05 = 5 %); > 2 % → waarschuwen bij vervangen. */
export function aspectDiff(
  a: { width: number | null; height: number | null } | null,
  b: { width: number; height: number },
) {
  if (!a?.width || !a.height) return 0;
  const ra = a.width / a.height;
  const rb = b.width / b.height;
  return Math.abs(ra - rb) / ra;
}

/** Opslag (R2) weigert of blokkeert de upload: vaak de CORS-regel van de bucket. */
export const STORAGE_UNREACHABLE =
  "Het bestand kon niet naar de opslag worden geüpload. Waarschijnlijk staat de CORS-regel van de R2-bucket niet goed; een beheerder kan dit controleren.";

/** PUT naar de presigned URL, met een begrijpelijke fout als de opslag niet meewerkt. */
export async function putToStorage(presign: PresignResponse, blob: Blob): Promise<void> {
  let put: Response;
  try {
    put = await fetch(presign.uploadUrl, { method: "PUT", headers: presign.headers, body: blob });
  } catch {
    // De API werkte net nog (presign), dus geen gewone verbindingsfout: de opslag blokkeert de upload.
    throw new ApiError(0, "storage_unreachable", STORAGE_UNREACHABLE);
  }
  if (!put.ok) throw new ApiError(put.status, "upload_failed", `De opslag weigerde de upload (HTTP ${put.status}).`);
}
